/*!
 * liveScenes.js — scene(drop, fade:8): a named #$ block as a scene, gone to
 * © 2026 Rob Canning — GPLv3
 *
 *     #$ intro                      a scene: the block under a head with a name
 *     synth(uid:pad, ...)
 *     #$ drop
 *     synth(uid:wall, ...)
 *
 *     scene(drop)                   to drop on the next bar: a cut
 *     scene(drop, fade:8)           ... crossfading over 8 beats
 *     scene(drop, fade:8, curve:exp3)   along level()'s curves
 *     scene(drop, quant:1)          on the next beat; quant:0 at once
 *     scene()                       the scenes, and which is on
 *
 *     scene(drop, fade:8, via:blur)     through an effect of the screen: swelling to its
 *                                       height at the middle, settling at the end --
 *                                       via:blur | warp | pixel | hue | invert | split
 *     scene(drop, fade:4, via:black)    through a colour: the stage veiled at the middle, the
 *                                       scenes changing over under it -- black, white, #f0f ...
 *     scene(drop, fade:4, via:wipe)     a line across the stage, each thing changing over as
 *                                       it passes (left to right; via:wipe-left the other way)
 * (via: with no fade: two beats.)
 *
 *     scenes(intro:16, drop:32, outro:8)          a sequence: each scene for so many beats, in turn,
 *     scenes(intro:16, drop:32, fade:4, via:blur, loop:1)   ... changing as scene() does; loop:1 round again
 *     scenes(off)                                 the sequence stopped (the scene on stays)
 * The sequence counts on the shared beat grid, so it does not drift; a scene()
 * run by hand takes over from it.
 *
 * What only the old scene made goes and what only the new one makes comes in
 * (liveScenePlan.js): its sounds by their faders (level(), from 0 up and down
 * to 0), then the old ones stopped; what it drew faded by its opacity, then
 * taken away -- as commenting its lines out and running them does. What both
 * make carries across, the new line changing it in place. Lines outside every
 * scene are no scene's: they play on through all of them.
 */

import { scenePlan, sceneSpans, waitBeats } from "./liveScenePlan.js";
import { setSceneRunner } from "../system/sceneHook.js";
import { gridNow, nextGridBoundary, beatAt, msAtBeat } from "../cues/audio/beatGrid.js";
import { secs } from "../cues/beats.js";
import { waveformsForElement } from "../system/waveform.js";
import { setPassing, PASSING_KEYS } from "../system/screenFx.js";
import { isDarkStage } from "../system/darkStage.js";

/** How far each screen effect goes at the middle of a transition through it. */
const VIA_PEAK = { blur: 30, warp: 120, pixel: 48, hue: 180, invert: 1, split: 40 };

const LEAD_MS = 80;             // run the block this far ahead of the bar: its patterns start on it (quant:1: 60 ms of lead); a plain synth sounds this early

let current = null;             // { name, exprs }: the scene on
let scheduled = null;           // a change waiting for its bar: { timer }
let fading = null;              // a change fading: { timer, finish }
let sequence = null;            // scenes(...) running: { steps, i, at, fade, via, curve, loop, timer }
let lastChange = null;          // { to, t0, sec }: for the corner's readout

/** What the console gives: its blocks, a way to run a line, its output. */
let io = { lines: () => [], blockExprs: () => [], run: () => false, log: () => {}, flash: () => {} };

export function initScenes(given) {
  io = { ...io, ...given };
  setSceneRunner(ast => (ast?.type === "cueScenes" ? sequenceCue(ast) : sceneCue(ast)));
}

/** A scene's lines, as they are in the buffer now: [expr], or null when there is no such block. */
function sceneBlock(name) {
  const span = sceneSpans(io.lines()).find(s => s.name === name);
  return span ? { head: span.head, exprs: io.blockExprs(span.head) } : null;
}

/** Everything drawn for a name: a shape, a box and what it draws, a text, a picture, film, the camera. */
function elementsOf(name) {
  const els = [];
  const add = el => { if (el && !els.includes(el)) els.push(el); };
  add(document.getElementById(name));
  const box = document.getElementById(`scope-${name}`);
  if (box) { add(box); for (const h of waveformsForElement(box)) add(h.group); }
  const q = CSS.escape(name);
  for (const el of document.querySelectorAll(`[data-uid="${q}"], .oscilla-image-holder[data-image-uid="${q}"]`)) add(el);
  return els;
}

/** Fade what is drawn for `names` to `to` over `sec` s (from `from` at once, if given); left as it was after. */
function fadeNames(names, to, sec, from = null) {
  fadeEls(names.flatMap(elementsOf), to, sec, from);
}

/** fadeNames for these elements. */
function fadeEls(els, to, sec, from = null) {
  for (const el of els) {
    const s = el.style;
    if (from != null) { s.transition = "none"; s.opacity = String(from); el.getBoundingClientRect(); }
    s.transition = `opacity ${sec}s linear`;
    // coming in: to its own opacity -- shape(opacity:0) stays unseen until
    // something fades it in (a score's fade(target:spot)), a box at 0.5 to 0.5
    s.opacity = String(to === 1 ? ownOpacity(el) : to);
  }
  // coming in: its own opacity again when there (a shape's fade, a viz()'s)
  if (to === 1) setTimeout(() => { for (const el of els) { el.style.transition = ""; el.style.opacity = ""; } }, sec * 1000 + 50);
}

/** The opacity an element was given (its opacity attribute), 1 if none. */
function ownOpacity(el) {
  const v = Number(el.getAttribute?.("opacity"));
  return el.hasAttribute?.("opacity") && Number.isFinite(v) ? v : 1;
}

/**
 * What the new scene draws that comes after its lines have run -- a sound's
 * box is made when the sound starts, a moment later -- found as it comes,
 * for `sec` s: given came(el, s), the seconds since the change began. Else
 * it shows at once at full, over a stage going dark. Returns the stop.
 */
function lateComers(names, sec, came) {
  const seen = new Set(names.flatMap(elementsOf));
  const t0 = performance.now();
  const look = () => {
    const t = (performance.now() - t0) / 1000;
    for (const el of names.flatMap(elementsOf)) if (!seen.has(el)) { seen.add(el); came(el, t); }
    if (t >= sec) clearInterval(timer);
  };
  const timer = setInterval(look, 30);
  return () => clearInterval(timer);
}

// ---- wait(): a block's lines in turn ---------------------------------------------

let waiting = [];               // [{ t, key }]: lines behind a wait(), a run being under way

/** The lines behind a wait() not run yet, dropped -- all of them (another scene, hush(), a new buffer), or one run's (its key). */
export function cancelWaits(key = null) {
  for (const w of waiting) if (key == null || w.key === key) clearTimeout(w.t);
  waiting = key == null ? [] : waiting.filter(w => w.key !== key);
}

/** fn in `beats` beats, dropped by cancelWaits() -- or cancelWaits(key), a run of the same block again. */
export function waitLater(beats, fn, key = "scene") {
  const t = setTimeout(() => { waiting = waiting.filter(w => w.t !== t); fn(); }, (Number(secs(beats)) || 0) * 1000);
  waiting.push({ t, key });
}
if (typeof window !== "undefined") window.addEventListener("oscilla:hush-all", cancelWaits);

/**
 * A block's lines run in turn: each wait(N) puts the lines after it N beats
 * later than the ones before (one after another adds up). The waiting lines
 * are dropped by cancelWaits() -- another scene, hush().
 */
function runInTurn(exprs, run) {
  let beats = 0;
  for (const e of exprs) {
    const w = waitBeats(e);
    if (w != null) { beats += Math.max(0, w); continue; }
    if (beats <= 0) { run(e); continue; }
    waitLater(beats, () => run(e));
  }
}

/** A fade under way finished now: the old scene's lines taken back. */
function finishFading() {
  if (!fading) return;
  clearTimeout(fading.timer);
  const f = fading;
  fading = null;
  f.finish();
}

function sceneCue(ast) {
  // a scene() by hand: a sequence running stops, this one is the last word
  stopSequence();
  const arg = k => ast?.args?.find(a => a.type === k)?.value;
  const name = arg("name") ?? arg("uid");
  const names = sceneSpans(io.lines()).map(s => s.name);
  if (name == null) {
    io.log(names.length ? `scenes: ${names.join(", ")}${current ? ` -- on: ${current.name}` : ""}` : "no scenes: a block under #$ name is one", "info");
    return true;
  }
  const block = sceneBlock(String(name));
  if (!block) { io.log(`scene(${name}): no block headed #$ ${name}${names.length ? ` (there are: ${names.join(", ")})` : ""}`, "err"); return false; }
  const via = arg("via") != null ? String(arg("via")).toLowerCase() : null;
  const fade = arg("cut") != null && !via ? 0 : Math.max(0, Number(arg("fade")) || 0) || (via ? 2 : 0);
  const curve = arg("curve") != null ? String(arg("curve")) : null;
  const quant = arg("quant") != null ? Math.max(0, Number(arg("quant")) || 0) : 4;

  // the last word: a change still waiting for its bar is dropped; one fading finishes now
  if (scheduled) { clearTimeout(scheduled.timer); scheduled = null; }
  const now = gridNow().ms;
  const at = quant > 0 ? nextGridBoundary(now + LEAD_MS + 30, quant) : now;
  const wait = Math.max(0, at - now - LEAD_MS);
  // add: on top of the scene that is on, nothing going (addLayer)
  const add = arg("add") != null;
  const go = () => { scheduled = null; if (add) addLayer(String(name), fade, curve); else change(String(name), fade, curve, via); };
  if (wait > 1) scheduled = { timer: setTimeout(go, wait) };
  else go();
  return true;
}

/** A frame loop for `sec` s: each frame f(t), t 0..1; f(1) at the end. Returns a stop that ends it at 1. */
function overTime(sec, f) {
  const t0 = performance.now();
  let raf = 0, done = false;
  const end = () => { if (done) return; done = true; cancelAnimationFrame(raf); f(1); };
  const frame = now => {
    const t = Math.min(1, (now - t0) / (sec * 1000));
    if (t >= 1) { end(); return; }
    f(t);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
  return end;
}

/** A veil of a colour over the stage (under the console): its opacity set by the caller. */
function veil(colour) {
  const el = document.createElement("div");
  el.id = "oscilla-scene-veil";
  // the page turned to its negative in dark mode: the veil turned back, its colour as written
  const dark = isDarkStage();
  el.style.cssText = `position:fixed;inset:0;pointer-events:none;z-index:10040;opacity:0;background:${colour};${dark ? "filter:invert(1) hue-rotate(180deg);" : ""}`;
  document.body.appendChild(el);
  return el;
}

/** Where across the stage a name's things are, 0..1 (the middle of them all), or null. */
function acrossOf(name) {
  const rs = elementsOf(name).map(el => el.getBoundingClientRect()).filter(r => r.width || r.height);
  if (!rs.length) return null;
  const l = Math.min(...rs.map(r => r.left)), r = Math.max(...rs.map(r => r.right));
  return Math.min(1, Math.max(0, (l + r) / 2 / (innerWidth || 1)));
}

/**
 * What is drawn changing over as a line crosses the stage: each name out (or in)
 * when the line reaches it. The line drawn too. Returns the stop.
 */
function wipe(outNames, inNames, sec, leftward) {
  const line = document.createElement("div");
  const dark = isDarkStage();
  line.style.cssText = `position:fixed;top:0;bottom:0;width:2px;pointer-events:none;z-index:10040;background:#fff;${dark ? "filter:invert(1);" : ""}`;
  document.body.appendChild(line);
  const at = n => { const x = acrossOf(n); return x == null ? 0.5 : (leftward ? 1 - x : x); };
  const outs = outNames.map(n => ({ n, x: at(n), done: false }));
  const ins = inNames.map(n => ({ n, x: at(n), done: false }));
  for (const i of ins) fadeNames([i.n], 0, 0);
  return overTime(sec, t => {
    line.style.left = `${(leftward ? 1 - t : t) * 100}%`;
    for (const o of outs) if (!o.done && t >= o.x) { o.done = true; fadeNames([o.n], 0, 0.12); }
    for (const i of ins) if (!i.done && t >= i.x) { i.done = true; fadeNames([i.n], 1, 0.12, 0); }
    if (t >= 1) line.remove();
  });
}

/** The lines of what is on: the scene's, and the blocks added to it (scene(s1b, add)), as they are in the buffer now. */
function exprsOn() {
  if (!current) return [];
  const own = sceneBlock(current.name)?.exprs ?? current.exprs;
  return [...own, ...(current.layers || []).flatMap(l => sceneBlock(l)?.exprs ?? [])];
}

/**
 * scene(s1b, add): the block's lines run on top of the scene that is on --
 * what it makes that is not there yet comes in (sounds from silence, what
 * it draws faded in), nothing goes -- and it is the scene's from then on:
 * the next scene() takes it away with the rest. Layers added one after
 * another build a section up (flowstates: a beat, then another, then another).
 */
function addLayer(name, fade, curve) {
  finishFading();
  const block = sceneBlock(name);
  if (!block) return;
  const run = line => io.run(line);
  const have = exprsOn();
  const plan = scenePlan(have, [...have, ...block.exprs]);
  const lvl = (s, to, beats) => run(`level(${s}, ${to}${beats ? `, ${beats}` : ""}${beats && curve ? `, ${curve}` : ""})`);
  const ins = [...plan.drawnIn, ...plan.soundsIn];
  if (!fade) {
    plan.soundsIn.forEach(s => lvl(s, 1));
    runInTurn(block.exprs, run);
    setTimeout(() => fadeNames(ins, 1, 0, 1), 60);
  } else {
    const sec = Number(secs(fade)) || 0;
    plan.soundsIn.forEach(s => lvl(s, 0));
    runInTurn(block.exprs, run);
    plan.soundsIn.forEach(s => lvl(s, 1, fade));
    setTimeout(() => fadeNames(ins, 1, Math.max(0, sec - 0.06), 0), 60);
    const stopLate = lateComers(ins, sec, (el, t) => fadeEls([el], 1, Math.max(0, sec - t), 0));
    fading = { timer: setTimeout(() => { fading = null; stopLate(); }, sec * 1000), finish: stopLate };
  }
  current = current
    ? { ...current, layers: [...new Set([...(current.layers || []), name])].filter(l => l !== current.name) }
    : { name, exprs: block.exprs, layers: [] };
  io.log(`scene: ${name} added${current.name !== name ? ` to ${current.name}` : ""}${fade ? ` (over ${fade} beats)` : ""}`, "ok");
  io.flash(block.head);
}

function change(name, fade, curve, via = null) {
  finishFading();
  const block = sceneBlock(name);
  if (!block) return;
  const run = line => io.run(line);
  // the same scene again, or another: what its last run still had waiting is dropped
  cancelWaits();
  // the same scene again: its lines run, nothing goes (what was added to it stays)
  if (current?.name === name) {
    runInTurn(block.exprs, run);
    current = { ...current, exprs: block.exprs };
    io.flash(block.head);
    return;
  }
  // what goes: the scene that is on, and every block added to it
  const oldExprs = exprsOn();
  const plan = scenePlan(oldExprs, block.exprs);
  const lvl = (s, to, beats) => run(`level(${s}, ${to}${beats ? `, ${beats}` : ""}${beats && curve ? `, ${curve}` : ""})`);
  if (!fade) {
    // a cut: the old scene's lines taken back, the new one's run -- its sounds at their own level
    plan.undo.forEach(run);
    plan.soundsIn.forEach(s => lvl(s, 1));
    runInTurn(block.exprs, run);
    setTimeout(() => fadeNames([...plan.drawnIn, ...plan.soundsIn], 1, 0, 1), 60);
  } else {
    // a crossfade: the new sounds from silence up, the old down; then the old taken back
    const sec = Number(secs(fade)) || 0;
    plan.soundsIn.forEach(s => lvl(s, 0));
    runInTurn(block.exprs, run);
    plan.soundsIn.forEach(s => lvl(s, 1, fade));
    plan.soundsOut.forEach(s => lvl(s, 0, fade));
    const outs = [...plan.drawnOut, ...plan.soundsOut], ins = [...plan.drawnIn, ...plan.soundsIn];
    let stopVia = () => {};
    if (via && (via === "wipe" || via === "wipe-left")) {
      // what the new lines draw is there a moment later (a viz() waits for its sound)
      setTimeout(() => { stopVia = wipe(outs, ins, Math.max(0.1, sec - 0.06), via === "wipe-left"); }, 60);
    } else if (via && !PASSING_KEYS.includes(via)) {
      // through a colour: the old out under the rising veil, the new in as it lifts
      const v = veil(via);
      fadeNames(outs, 0, sec / 2);
      setTimeout(() => fadeNames(ins, 1, sec / 2, 0), sec * 500);
      fadeNames(ins, 0, 0);
      // come late: unseen until the veil lifts (the fade in at the middle takes it), after it in over the time left
      const stopLate = lateComers(ins, sec, (el, t) => (t < sec / 2 ? fadeEls([el], 0, 0) : fadeEls([el], 1, Math.max(0, sec - t), 0)));
      const stopVeil = overTime(sec, t => { v.style.opacity = String(Math.sin(Math.PI * t)); if (t >= 1) v.remove(); });
      stopVia = () => { stopLate(); stopVeil(); };
    } else {
      fadeNames(outs, 0, sec);
      setTimeout(() => fadeNames(ins, 1, Math.max(0, sec - 0.06), 0), 60);
      // come late: in from nothing over the time left, as the rest is
      const stopLate = lateComers(ins, sec, (el, t) => fadeEls([el], 1, Math.max(0, sec - t), 0));
      // through a screen effect: swelling to its height at the middle, gone at the end
      const stopFx = via ? overTime(sec, t => setPassing(via, t >= 1 ? 0 : VIA_PEAK[via] * Math.sin(Math.PI * t))) : () => {};
      stopVia = () => { stopLate(); stopFx(); };
    }
    const finish = () => { stopVia(); plan.undo.forEach(run); };
    fading = { timer: setTimeout(() => { fading = null; finish(); }, sec * 1000), finish };
  }
  io.log(`scene: ${name}${fade ? ` (${via ? `via ${via}, ` : ""}over ${fade} beats)` : ""}`, "ok");
  lastChange = { to: name, t0: performance.now(), sec: fade ? Number(secs(fade)) || 0 : 0 };
  io.flash(block.head);
  current = { name, exprs: block.exprs };
}

// ---- scenes(...): a sequence -------------------------------------------------------

const SEQ_KEYS = new Set(["fade", "via", "curve", "quant", "loop", "off"]);

function stopSequence() {
  if (sequence) clearTimeout(sequence.timer);
  sequence = null;
}

function sequenceCue(ast) {
  const args = ast?.args || [];
  const opt = k => args.find(a => a.type === k)?.value;
  if (opt("off") != null) { stopSequence(); io.log("scenes: the sequence stopped (the scene on stays)", "ok"); return true; }
  const steps = args.filter(a => !SEQ_KEYS.has(a.type)).map(a => ({ name: a.type, beats: Math.max(0.25, Number(a.value) || 0) }));
  if (!steps.length) { io.log("scenes(intro:16, drop:32, fade:4, loop:1): each scene for so many beats", "err"); return false; }
  const missing = steps.filter(s => !sceneBlock(s.name)).map(s => s.name);
  if (missing.length) { io.log(`scenes: no block headed #$ ${missing.join(", #$ ")}`, "err"); return false; }
  stopSequence();
  if (scheduled) { clearTimeout(scheduled.timer); scheduled = null; }
  const quant = opt("quant") != null ? Math.max(0, Number(opt("quant")) || 0) : 4;
  const now = gridNow().ms;
  const firstMs = quant > 0 ? nextGridBoundary(now + LEAD_MS + 30, quant) : now + LEAD_MS;
  const on = v => v != null && !/^(0|off|false|no)$/i.test(String(v));
  sequence = {
    steps, i: -1, at: beatAt(firstMs), timer: null,
    fade: Math.max(0, Number(opt("fade")) || 0),
    via: opt("via") != null ? String(opt("via")).toLowerCase() : null,
    curve: opt("curve") != null ? String(opt("curve")) : null,
    loop: on(opt("loop")),
  };
  stepAt(0, sequence.at);
  io.log(`scenes: ${steps.map(s => `${s.name} ${s.beats}`).join(" -> ")}${sequence.loop ? ", round again" : ""}`, "ok");
  return true;
}

/** Step `i` of the sequence on beat `beat` of the grid (the next one after it, and so on). */
function stepAt(i, beat) {
  const seq = sequence;
  if (!seq) return;
  if (i >= seq.steps.length) {
    if (!seq.loop) { sequence = null; return; }
    i = 0;
  }
  const wait = Math.max(0, msAtBeat(beat) - gridNow().ms - LEAD_MS);
  seq.timer = setTimeout(() => {
    if (sequence !== seq) return;
    const s = seq.steps[i];
    seq.i = i;
    seq.at = beat;
    // into the first scene at once; between scenes as asked (via: with no fade: two beats)
    const fade = current ? (seq.fade || (seq.via ? 2 : 0)) : 0;
    change(s.name, fade, seq.curve, current ? seq.via : null);
    stepAt(i + 1, beat + s.beats);
  }, wait);
}

/**
 * Where the scenes are, for the corner's list (live.js): the buffer's scenes,
 * the one on, a change under way (0..1 through), the sequence and where in it.
 */
export function sceneStatus() {
  const names = sceneSpans(io.lines()).map(s => s.name);
  const t = lastChange && lastChange.sec > 0 ? Math.min(1, (performance.now() - lastChange.t0) / (lastChange.sec * 1000)) : 1;
  const nowBeat = beatAt(gridNow().ms);
  return {
    names,
    current: current?.name ?? null,
    changing: t < 1 ? { to: lastChange.to, t } : null,
    sequence: sequence && sequence.i >= 0 ? {
      steps: sequence.steps.map(s => `${s.name} ${s.beats}`),
      i: sequence.i,
      beatsLeft: Math.max(0, sequence.at + sequence.steps[sequence.i].beats - nowBeat),
      loop: sequence.loop,
    } : null,
  };
}

/** A new buffer, an example: no scene on, nothing waiting. */
export function resetScenes() {
  cancelWaits();
  if (scheduled) clearTimeout(scheduled.timer);
  if (fading) clearTimeout(fading.timer);
  stopSequence();
  scheduled = fading = current = lastChange = null;
}
